import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, redirect } from "react-router-dom";
import {updateUserInfo, userInfo, updateToken, getEbooks} from './../../app/api';
import { UserContext } from "../../providers/UserProvider";
import OrderInfo from "./order";
import { QRCode } from "react-qrcode-logo";
import { Disclosure } from "@headlessui/react";
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom";

export const UserProfile = () => {
  const nav = useNavigate();
  const {userBio} = useContext(UserContext);
  useEffect(() => {
    if(userBio) {
      nav(`/profile/${userBio.id}`);
    }
  }, [userBio]);

  return (<></>);
}

export const Profile = () => {
  const { t } = useTranslation();
  const {role} = useContext(UserContext);
  const {id} = useParams();

  const [profileBio, setProfileBio] = useState<any | undefined>(undefined);
  const [orders, setOrders] = useState<any[]>([]);
  const [purchasedEbooks, setPurchasedEbooks] = useState<any[]>([]);

  const [profileUpdateResponse, setProfileUpdateResponse] = useState<string | undefined>(undefined);
  const [profileUpdateError, setProfileUpdateError] = useState<string | undefined>(undefined);
  const [profileEdit, setProfileEdit] = useState(false);
  const [editImage, setEditImage] = useState<any | undefined>(undefined);
  const [profileEditLoading, setProfileEditLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchProfileBio = useCallback(async () => {
    if(id) {
      setLoading(true);
      try {
        updateToken();
        const resp = await userInfo(id);
        setProfileBio(resp.data.user);
        setOrders(resp.data.order);
        // Fetch purchased ebooks
        try {
          const ebookResp = await getEbooks({ limit: 100, offset: 0 });
          const allEbooks = ebookResp.data.results || [];
          // Filter purchased ones by checking each ebook detail
          setPurchasedEbooks(allEbooks.filter((e: any) => e.purchased));
        } catch(e) {
          // Silent fail for ebooks
        }
      } catch(err) {
        console.log(err);
      }
      setLoading(false);
    }
  }, [id]);

  const handleProfileUpdate = useCallback(async () => {
    setProfileEditLoading(true);
    try {
      const formData = new FormData();

      formData.set('address', profileBio.address);
      formData.set('parentPhone', profileBio.parentPhone);
      formData.set('school', profileBio.school);
      formData.set('grade', profileBio.grade);
      if(editImage) {
        formData.set('image', editImage);
      }

      const resp = await updateUserInfo(id, formData);
      setProfileUpdateError(undefined);
      setProfileUpdateResponse(t('profile.updateSuccess'));
      fetchProfileBio();
      setProfileEdit(false);
    } catch(err: any) {
      const resp = err.response;
      setProfileUpdateResponse(undefined);
      setProfileUpdateError(resp.data.error);
    }
    setProfileEditLoading(false);
  }, [id, profileBio]);

  const profileQR = useMemo(() => {
    if(id) {
      return (
        <div>
          <QRCode
            value={`https://nairamdal.mn/profile/${id}`}
            size={220}
            ecLevel="H"
            logoImage="/qr_logo.png"
            logoPaddingStyle="circle"
            logoPadding={10}
            removeQrCodeBehindLogo
            logoWidth={80}
          />
        </div>
      )
    }
    return <></>
  }, [id]);

  useEffect(() => {
    try {
      fetchProfileBio();
    } catch(err) {
      console.log(err);
    }
  }, [id]);

  return (
    <>
      {loading && (
        <div className="w-full h-full fixed top-0 left-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="relative">
            <div className="w-20 h-20 border-2 border-neutral-200 border-t-primary-500 rounded-full animate-spin"></div>
          </div>
        </div>
      )}

      {/* Dark Hero Header */}
      <div className="relative overflow-hidden bg-[#0a1628] -mt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-primary-600/20 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-accent-500/15 blur-[100px]" />
        </div>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
        <div className="relative pt-40 pb-16 sm:pt-48 sm:pb-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-accent-300 backdrop-blur-sm">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse" />
              {t('profile.badge')}
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
              {t('profile.title')}
            </h1>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="bg-neutral-50 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-6 mb-12">
            {orders.map((order, index) => (
              <Disclosure key={order.id} defaultOpen>
                {({open}) => (
                  <>
                    <Disclosure.Button className="w-full bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 p-6 mb-4 animate-slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
                      <div className="flex w-full justify-between items-center">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-primary-600 flex items-center justify-center text-white shadow-md">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>
                          </div>
                          <p className="text-lg sm:text-xl font-bold text-neutral-900">{order.eelj?.title}</p>
                        </div>
                        <div className="px-6 py-3 rounded-xl bg-primary-600 text-white font-semibold text-sm shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300">
                          {open ? t('profile.close') : t('profile.open')}
                        </div>
                      </div>
                    </Disclosure.Button>
                    <Disclosure.Panel className="animate-fade-in">
                      <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
                        <OrderInfo key={order.id} {...order} userRole={role}/>
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ))}
          </div>
          {/* Purchased Ebooks Section */}
          {purchasedEbooks.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-neutral-900 mb-6">{t('ebook.badge')}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {purchasedEbooks.map((ebook: any) => (
                  <Link key={ebook._id || ebook.id} to={`/ebook/read/${ebook._id || ebook.id}`} className="group block">
                    <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden">
                      <div className="aspect-[3/4] overflow-hidden bg-neutral-100">
                        <img
                          src={ebook.cover}
                          alt={ebook.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="font-bold text-neutral-900 text-sm line-clamp-2">{ebook.title}</h3>
                        <p className="text-xs text-neutral-500 mt-1">{ebook.author}</p>
                        <span className="inline-block mt-2 text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full">{t('ebook.read')}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {profileBio && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 animate-slide-up">
              <form>
                <div className="grid lg:grid-cols-3 gap-8 mb-8">
                  <div className="lg:col-span-1">
                    <div className="space-y-6">
                      <div
                        style={{
                          backgroundImage: `url(${profileBio.image ?? ''})`
                        }}
                        className="relative bg-cover bg-center bg-no-repeat w-full aspect-square bg-primary-100 rounded-2xl shadow-md"
                      />
                      <div className="bg-white rounded-2xl p-4 shadow-sm">
                        {profileQR}
                      </div>
                    </div>
                  </div>

                  <div className="lg:col-span-2">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900">
                        {t('profile.childInfo')}
                      </h2>
                      {role === 'user' && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            setProfileEdit(!profileEdit);
                          }}
                          className={`px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg hover:scale-105 transform transition-all duration-300 ${
                            profileEdit
                              ? 'bg-neutral-200 text-neutral-700'
                              : 'bg-primary-600 text-white'
                          }`}
                        >
                          {profileEdit ? t('profile.cancel') : t('profile.edit')}
                        </button>
                      )}
                    </div>

                    {profileEdit && (
                      <div className="mb-6 p-6 bg-primary-50 rounded-2xl border border-primary-200">
                        <p className="font-bold text-neutral-900 mb-4">{t('profile.newPhoto')}</p>
                        <div className="flex flex-col items-center justify-center w-full">
                          <label
                            htmlFor="dropzone-file"
                            className="flex flex-col items-center justify-center w-full h-64 border-2 border-primary-300 border-dashed rounded-2xl cursor-pointer bg-white hover:border-primary-500 transition-all"
                          >
                            {!editImage ? (
                              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <div className="w-16 h-16 rounded-full bg-primary-600 flex items-center justify-center mb-4 shadow-md">
                                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                </div>
                                <p className="mb-2 text-sm font-semibold text-neutral-900">
                                  {t('profile.photoUpload')} <span className="text-primary-600 underline">{t('profile.photoSelect')}</span>
                                </p>
                                <p className="text-xs text-neutral-500">
                                  {t('profile.photoFormat')}
                                </p>
                              </div>
                            ) : (
                              <img src={URL.createObjectURL(editImage)} className="max-h-56 rounded-xl shadow-md" alt="Preview" />
                            )}
                            <input
                              id="dropzone-file"
                              type="file"
                              className="hidden"
                              accept="image/*"
                              onChange={(e) => {
                                if(e.target.files) {
                                  setEditImage(e.target.files[0]);
                                }
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-6">
                      <label className="block">
                        <span className="block text-sm font-bold text-neutral-900 mb-2">{t('profile.lastName')}</span>
                        <input
                          disabled
                          className="w-full px-4 py-3 rounded-xl border border-neutral-200 bg-neutral-50 text-neutral-700"
                          placeholder={t('profile.lastName')}
                          type="text"
                          value={profileBio.lastName}
                        />
                      </label>
                      <label className="block">
                        <span className="block text-sm font-bold text-neutral-900 mb-2">{t('profile.firstName')}</span>
                        <input
                          disabled
                          className="w-full px-4 py-3 rounded-xl border border-neutral-200 bg-neutral-50 text-neutral-700"
                          placeholder={t('profile.firstName')}
                          type="text"
                          value={profileBio.firstName}
                        />
                      </label>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6 mt-6">
                      <label className="block">
                        <span className="block text-sm font-bold text-neutral-900 mb-2">{t('profile.registerNumber')}</span>
                        <input
                          disabled
                          className="w-full px-4 py-3 rounded-xl border border-neutral-200 bg-neutral-50 text-neutral-700"
                          placeholder={t('profile.registerNumber')}
                          type="text"
                          value={profileBio.registerNumber}
                        />
                      </label>
                      <label className="block">
                        <span className="block text-sm font-bold text-neutral-900 mb-2">{t('profile.birthDate')}</span>
                        <input
                          disabled
                          className="w-full px-4 py-3 rounded-xl border border-neutral-200 bg-neutral-50 text-neutral-700"
                          placeholder={t('profile.birthDate')}
                          type="text"
                          value={profileBio.birthDate ? new Date(profileBio.birthDate).toISOString().split('T')[0]: ''}
                        />
                      </label>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 mt-6">
                      <label className="block">
                        <span className="block text-sm font-bold text-neutral-900 mb-2">{t('profile.phone')}</span>
                        <input
                          disabled
                          className="w-full px-4 py-3 rounded-xl border border-neutral-200 bg-neutral-50 text-neutral-700"
                          placeholder={t('profile.phone')}
                          type="tel"
                          value={profileBio.phone}
                        />
                      </label>
                      <label className="block">
                        <span className="block text-sm font-bold text-neutral-900 mb-2">{t('profile.email')}</span>
                        <input
                          disabled
                          className="w-full px-4 py-3 rounded-xl border border-neutral-200 bg-neutral-50 text-neutral-700"
                          placeholder={t('profile.email')}
                          type="text"
                          value={profileBio.email}
                        />
                      </label>
                    </div>

                    <label className="block mt-6">
                      <span className="block text-sm font-bold text-neutral-900 mb-2">{t('profile.gender')}</span>
                      <input
                        disabled
                        className="w-full px-4 py-3 rounded-xl border border-neutral-200 bg-neutral-50 text-neutral-700"
                        placeholder={t('profile.gender')}
                        type="text"
                        value={profileBio.gender}
                      />
                    </label>

                    <label className="block mt-6">
                      <span className="block text-sm font-bold text-neutral-900 mb-2">{t('profile.address')}</span>
                      <input
                        disabled={!profileEdit || role !== 'user'}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          !profileEdit || role !== 'user'
                            ? 'border-neutral-200 bg-neutral-50 text-neutral-700'
                            : 'border-neutral-200 focus:border-primary-500 bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500'
                        } transition-all`}
                        type="text"
                        value={profileBio.address}
                        onChange={(e) => {
                          setProfileBio({
                            ...profileBio,
                            address: e.target.value
                          });
                        }}
                      />
                    </label>

                    <div className="grid md:grid-cols-2 gap-6 mt-6">
                      <label className="block">
                        <span className="block text-sm font-bold text-neutral-900 mb-2">{t('profile.school')}</span>
                        <input
                          disabled={!profileEdit || role !== 'user'}
                          className={`w-full px-4 py-3 rounded-xl border ${
                            !profileEdit || role !== 'user'
                              ? 'border-neutral-200 bg-neutral-50 text-neutral-700'
                              : 'border-neutral-200 focus:border-primary-500 bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500'
                          } transition-all`}
                          type="text"
                          value={profileBio.school}
                          onChange={(e) => {
                            setProfileBio({
                              ...profileBio,
                              school: e.target.value
                            });
                          }}
                        />
                      </label>
                      <label className="block">
                        <span className="block text-sm font-bold text-neutral-900 mb-2">{t('profile.grade')}</span>
                        <input
                          disabled={!profileEdit || role !== 'user'}
                          className={`w-full px-4 py-3 rounded-xl border ${
                            !profileEdit || role !== 'user'
                              ? 'border-neutral-200 bg-neutral-50 text-neutral-700'
                              : 'border-neutral-200 focus:border-primary-500 bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500'
                          } transition-all`}
                          placeholder={t('profile.grade')}
                          type="text"
                          value={profileBio.grade}
                          onChange={(e) => {
                            setProfileBio({
                              ...profileBio,
                              grade: e.target.value
                            });
                          }}
                        />
                      </label>
                    </div>

                    <label className="block mt-6">
                      <span className="block text-sm font-bold text-neutral-900 mb-2">{t('profile.parentPhone')}</span>
                      <input
                        disabled={!profileEdit || role !== 'user'}
                        className={`w-full px-4 py-3 rounded-xl border ${
                          !profileEdit || role !== 'user'
                            ? 'border-neutral-200 bg-neutral-50 text-neutral-700'
                            : 'border-neutral-200 focus:border-primary-500 bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500'
                        } transition-all`}
                        type="text"
                        value={profileBio.parentPhone}
                        onChange={(e) => {
                          setProfileBio({
                            ...profileBio,
                            parentPhone: e.target.value
                          });
                        }}
                      />
                    </label>
                    {profileEdit && (
                      <div className="mt-6">
                        {profileEditLoading ? (
                          <button disabled className="px-8 py-4 rounded-2xl bg-neutral-200 text-neutral-500 font-semibold cursor-not-allowed">
                            <span className="inline-block animate-spin mr-2">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                            </span>
                            {t('profile.submitting')}
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleProfileUpdate();
                            }}
                            className="btn-primary px-8 py-4"
                          >
                            {t('profile.submit')}
                          </button>
                        )}
                      </div>
                    )}

                    <div className="mt-6">
                      {profileUpdateResponse && (
                        <div className="bg-primary-50 border border-primary-200 text-primary-700 px-6 py-4 rounded-2xl">
                          <div className="flex items-center gap-3">
                            <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <span className="font-semibold">{profileUpdateResponse}</span>
                          </div>
                        </div>
                      )}
                      {profileUpdateError && (
                        <div className="bg-danger-50 border border-danger-200 text-danger-700 px-6 py-4 rounded-2xl mt-4">
                          <div className="flex items-center gap-3">
                            <svg className="w-6 h-6 text-danger-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                            <span className="font-semibold">{profileUpdateError}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default Profile;
